/*
  <be-hero-personalizer>: homepage "Try it" hero (sections/be-hero-personalizer.liquid).

  Drives an unbound <engraving-preview> from its own controls and keeps both CTAs
  pointing at the wallet card product for the chosen metal, carrying the engraving
  along with window.BEEngraving.buildUrl (assets/engraving-preview.js).

  The sample text only travels to the product page once the shopper has edited it,
  so nobody lands on a product page with "For Daniel" already filled in.
*/
(function () {
  'use strict';

  if (!('customElements' in window) || customElements.get('be-hero-personalizer')) return;

  var ALIGN_LABELS = { left: 'Left', center: 'Center', right: 'Right' };
  var SIZE_LABELS = { s: 'Small', m: 'Medium', l: 'Large' };
  var METAL_LABELS = { aluminum: 'Aluminum', stainless: 'Stainless steel', brass: 'Brass', copper: 'Copper' };

  function clamp(text, maxLines, maxChars) {
    return String(text || '')
      .replace(/\r\n?/g, '\n')
      .split('\n')
      .slice(0, maxLines)
      .map(function (line) { return line.slice(0, maxChars); })
      .join('\n');
  }

  class BeHeroPersonalizer extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.maxLines = parseInt(this.dataset.maxLines, 10) || 3;
      this.maxChars = parseInt(this.dataset.maxChars, 10) || 30;
      this.textarea = this.querySelector('[data-be-hero-text]');
      this.ctas = Array.prototype.slice.call(this.querySelectorAll('[data-be-hero-cta]'));
      this.urls = {};
      var urlsEl = this.querySelector('[data-be-hero-urls]');
      if (urlsEl) {
        try { this.urls = JSON.parse(urlsEl.textContent) || {}; } catch (e) { this.urls = {}; }
      }

      var selected = function (root, sel, attr, fallback) {
        var el = root.querySelector(sel);
        return el ? el.getAttribute(attr) : fallback;
      };
      this.state = {
        front: this.textarea ? clamp(this.textarea.value, this.maxLines, this.maxChars) : '',
        metal: selected(this, '[data-metal-btn].is-selected', 'data-metal-btn', this.dataset.defaultMetal || 'stainless'),
        fontValue: selected(this, '[data-font-btn].is-selected', 'data-font-btn', 'Sans Serif (Optima)'),
        align: selected(this, '[data-align-btn].is-active', 'data-align-btn', 'left'),
        size: selected(this, '[data-size-btn].is-active', 'data-size-btn', 'm')
      };
      this.textEdited = false;
      this.styleTouched = false;

      this.bind();
      this.render();

      var self = this;
      customElements.whenDefined('engraving-preview').then(function () {
        self.preview = self.querySelector('engraving-preview');
        if (window.BEEngraving && window.BEEngraving.loadGoogleFont) {
          // The font chips show each face in its own family.
          window.BEEngraving.loadGoogleFont(['Marcellus', 'EB Garamond']);
        }
        self.render();
      });
    }

    bind() {
      var self = this;

      if (this.textarea) {
        this.textarea.addEventListener('input', function () {
          var ta = self.textarea;
          var clamped = clamp(ta.value, self.maxLines, self.maxChars);
          if (clamped !== ta.value) {
            var pos = Math.min(typeof ta.selectionStart === 'number' ? ta.selectionStart : clamped.length, clamped.length);
            ta.value = clamped;
            try { ta.setSelectionRange(pos, pos); } catch (e) { /* not focusable */ }
          }
          self.state.front = clamped;
          self.textEdited = true;
          self.render();
        });
      }

      this.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-metal-btn], [data-font-btn], [data-align-btn], [data-size-btn]');
        if (!btn || !self.contains(btn)) return;
        if (btn.hasAttribute('data-metal-btn')) {
          self.state.metal = btn.getAttribute('data-metal-btn');
        } else if (btn.hasAttribute('data-font-btn')) {
          self.state.fontValue = btn.getAttribute('data-font-btn');
          self.styleTouched = true;
        } else if (btn.hasAttribute('data-align-btn')) {
          self.state.align = btn.getAttribute('data-align-btn');
          self.styleTouched = true;
        } else {
          self.state.size = btn.getAttribute('data-size-btn');
          self.styleTouched = true;
        }
        self.render();
      });
    }

    toggle(selector, attr, value, cls) {
      this.querySelectorAll(selector).forEach(function (el) {
        var on = el.getAttribute(attr) === value;
        el.classList.toggle(cls, on);
        el.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    productUrl() {
      return this.urls[this.state.metal] || this.urls[this.dataset.defaultMetal] || this.dataset.fallbackUrl || '/collections/all';
    }

    render() {
      var s = this.state;

      this.toggle('[data-metal-btn]', 'data-metal-btn', s.metal, 'is-selected');
      this.toggle('[data-font-btn]', 'data-font-btn', s.fontValue, 'is-selected');
      this.toggle('[data-align-btn]', 'data-align-btn', s.align, 'is-active');
      this.toggle('[data-size-btn]', 'data-size-btn', s.size, 'is-active');

      var metalLabel = METAL_LABELS[s.metal] || '';
      var btn = this.querySelector('[data-metal-btn="' + s.metal + '"]');
      if (btn && btn.dataset.label) metalLabel = btn.dataset.label;
      this.querySelectorAll('[data-be-metal-label]').forEach(function (el) { el.textContent = metalLabel; });

      var caption = this.querySelector('[data-be-layout-caption]');
      if (caption) caption.textContent = (ALIGN_LABELS[s.align] || 'Left') + ' · ' + (SIZE_LABELS[s.size] || 'Medium');

      if (this.preview && typeof this.preview.update === 'function') {
        this.preview.update({ front: s.front, fontValue: s.fontValue, align: s.align, size: s.size, metal: s.metal });
      }

      var base = this.productUrl();
      var BE = window.BEEngraving;
      var href = base;
      if (BE && typeof BE.buildUrl === 'function' && (this.textEdited || this.styleTouched)) {
        var carry = {};
        if (this.textEdited) carry.front = s.front;
        carry.fontValue = s.fontValue;
        carry.align = s.align;
        carry.size = s.size;
        href = BE.buildUrl(base, carry);
      }
      this.ctas.forEach(function (a) { a.setAttribute('href', href); });
    }
  }

  customElements.define('be-hero-personalizer', BeHeroPersonalizer);
})();
